import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Slider,
  ToggleButton,
} from '@mui/material';
import {
  Video,
  Square,
  VideoOff,
  Circle,
  Mic,
  MicOff,
  Pause,
  Play,
  Maximize,
  Youtube,
  Instagram,
  RectangleVertical,
  RectangleHorizontal,
  Square as SquareIcon,
  Edit,
  Palette,
  Undo2,
  X,
  Circle as CircleIcon,
  RectangleHorizontal as Rectangle,
} from 'lucide-mui';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  MediaRecorderState,
  ProgressModal,
  uploadToScreenApp,
  initializeMediaRecorder,
  stopMediaRecording,
  finishProcessing,
  useIframeResizer,
} from './common';

interface AspectRatioOption {
  id: string;
  label: string;
  ratio: string;
  icon: React.ReactNode;
  width: number;
  height: number;
}

const aspectRatioOptions: AspectRatioOption[] = [
  {
    id: 'landscape',
    label: 'Landscape',
    ratio: '16:9',
    icon: <RectangleHorizontal />,
    width: 1920,
    height: 1080,
  },
  {
    id: 'square',
    label: 'Square',
    ratio: '1:1',
    icon: <SquareIcon />,
    width: 1080,
    height: 1080,
  },
  {
    id: 'portrait',
    label: 'Portrait',
    ratio: '4:5',
    icon: <RectangleVertical />,
    width: 1080,
    height: 1350,
  },
  {
    id: 'tall',
    label: 'Tall Portrait',
    ratio: '9:16',
    icon: <RectangleVertical />,
    width: 1080,
    height: 1920,
  },
  {
    id: 'youtube',
    label: 'YouTube',
    ratio: '16:9',
    icon: <Youtube />,
    width: 1920,
    height: 1080,
  },
  {
    id: 'youtube-short',
    label: 'YouTube Short',
    ratio: '9:16',
    icon: <Youtube />,
    width: 1080,
    height: 1920,
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    ratio: '9:16',
    icon: <RectangleVertical />,
    width: 1080,
    height: 1920,
  },
  {
    id: 'instagram-post',
    label: 'Instagram Post',
    ratio: '1:1',
    icon: <Instagram />,
    width: 1080,
    height: 1080,
  },
  {
    id: 'instagram-story',
    label: 'Instagram Story',
    ratio: '9:16',
    icon: <Instagram />,
    width: 1080,
    height: 1920,
  },
];

interface DrawingState {
  isDrawing: boolean;
  lastX: number;
  lastY: number;
  color: string;
  lineWidth: number;
  paths: Path2D[];
  tool: 'pen' | 'circle' | 'rectangle';
  startX: number;
  startY: number;
}

interface ScreenRecorderState extends MediaRecorderState {
  showWebcam: boolean;
  isMuted: boolean;
  isPaused: boolean;
  selectedAspectRatio: AspectRatioOption;
  isDrawingMode: boolean;
}

interface MenuItemBase {
  id: string;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: (e?: React.MouseEvent<HTMLElement>) => void;
  color?: string;
  show: (state: ScreenRecorderState) => boolean;
  disabled?: (
    state: ScreenRecorderState,
    drawingState: DrawingState
  ) => boolean;
  dividerAfter?: boolean;
  buttonStyle?: 'action' | 'toggle';
}

const ScreenRecorder: React.FC = () => {
  const { t: translate } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<ScreenRecorderState>({
    isRecording: false,
    showWebcam: false,
    isMuted: false,
    isPaused: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    selectedAspectRatio: aspectRatioOptions[0],
    isDrawingMode: false,
  });

  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
    lastX: 0,
    lastY: 0,
    color: '#F44336',
    lineWidth: 5,
    paths: [],
    tool: 'pen',
    startX: 0,
    startY: 0,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const recordedBlob = useRef<Blob | null>(null);
  const displayStream = useRef<MediaStream | null>(null);
  const audioStream = useRef<MediaStream | null>(null);
  const webcamStream = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLVideoElement>(null);
  const webcamRef = useRef<HTMLVideoElement>(null);
  const animationFrame = useRef<number | null>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);

  const drawFrame = () => {
    if (
      !canvasRef.current ||
      !previewRef.current ||
      !mediaRecorder.current ||
      mediaRecorder.current.state !== 'recording'
    )
      return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Clear the canvas before drawing
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    // Calculate dimensions to maintain aspect ratio
    const videoAspect =
      previewRef.current.videoWidth / previewRef.current.videoHeight;
    const canvasAspect = canvasRef.current.width / canvasRef.current.height;

    let sx = 0;
    let sy = 0;
    let sWidth = previewRef.current.videoWidth;
    let sHeight = previewRef.current.videoHeight;

    if (videoAspect > canvasAspect) {
      // Video is wider than canvas
      sWidth = previewRef.current.videoHeight * canvasAspect;
      sx = (previewRef.current.videoWidth - sWidth) / 2;
    } else {
      // Video is taller than canvas
      sHeight = previewRef.current.videoWidth / canvasAspect;
      sy = (previewRef.current.videoHeight - sHeight) / 2;
    }

    ctx.drawImage(
      previewRef.current,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );

    if (state.showWebcam && webcamRef.current) {
      const size =
        Math.min(canvasRef.current.width, canvasRef.current.height) / 5;
      const padding = 20;
      const webcamAspectRatio =
        webcamRef.current.videoWidth / webcamRef.current.videoHeight;
      const cropWidth =
        webcamAspectRatio > 1
          ? webcamRef.current.videoHeight
          : webcamRef.current.videoWidth;
      const cropHeight = cropWidth;

      const sourceX = (webcamRef.current.videoWidth - cropWidth) / 2;
      const sourceY = (webcamRef.current.videoHeight - cropHeight) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.arc(
        size / 2 + padding,
        canvasRef.current.height - size / 2 - padding,
        size / 2,
        0,
        Math.PI * 2
      );
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        webcamRef.current,
        sourceX,
        sourceY,
        cropWidth,
        cropHeight,
        padding,
        canvasRef.current.height - size - padding,
        size,
        size
      );
      ctx.restore();
    }

    // Draw the drawings from the drawing canvas
    if (drawingCanvasRef.current) {
      ctx.drawImage(drawingCanvasRef.current, 0, 0);
    }

    animationFrame.current = requestAnimationFrame(drawFrame);
  };

  const handleAspectRatioClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAspectRatioClose = () => {
    setAnchorEl(null);
  };

  const handleAspectRatioSelect = (option: AspectRatioOption) => {
    setState((prev) => ({ ...prev, selectedAspectRatio: option }));
    handleAspectRatioClose();

    // Update canvas dimensions if recording is active
    if (canvasRef.current && displayStream.current) {
      canvasRef.current.width = option.width;
      canvasRef.current.height = option.height;
    }
  };

  const startRecording = async () => {
    try {
      // Get display media stream with specific dimensions
      displayStream.current = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 30 },
          width: { ideal: state.selectedAspectRatio.width },
          height: { ideal: state.selectedAspectRatio.height },
          aspectRatio: {
            ideal:
              state.selectedAspectRatio.width /
              state.selectedAspectRatio.height,
          },
        },
      });

      // Get audio stream
      audioStream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      // Set initial audio state
      if (state.isMuted && audioStream.current) {
        const tracks = audioStream.current.getAudioTracks();
        tracks.forEach((track) => {
          const newTrack = track.clone();
          newTrack.enabled = false;
          audioStream.current?.addTrack(newTrack);
          track.stop();
        });
      }

      // Get webcam stream if enabled
      if (state.showWebcam) {
        webcamStream.current = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
      }

      // Set up canvas and preview elements
      if (!canvasRef.current || !previewRef.current) return;
      const canvas = canvasRef.current;
      const preview = previewRef.current;

      // Set canvas dimensions based on selected aspect ratio
      canvas.width = state.selectedAspectRatio.width;
      canvas.height = state.selectedAspectRatio.height;

      // Set up preview stream
      preview.srcObject = displayStream.current;
      preview.play();

      // Set up webcam preview if enabled
      if (state.showWebcam && webcamRef.current && webcamStream.current) {
        webcamRef.current.srcObject = webcamStream.current;
        webcamRef.current.play();
      }

      // Create MediaRecorder
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const canvasStream = canvas.captureStream(30);
      const audioTracks = audioStream.current.getAudioTracks();
      audioTracks.forEach((audioTrack) => canvasStream.addTrack(audioTrack));

      mediaRecorder.current = new MediaRecorder(canvasStream, {
        mimeType: 'video/webm;codecs=vp8,opus',
      });

      // Initialize media recorder with shared utility
      initializeMediaRecorder(mediaRecorder, chunks, (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      });

      // Start recording
      mediaRecorder.current.start(200);
      setState((prev) => ({ ...prev, isRecording: true }));

      // Start animation frame loop
      drawFrame();

      // Set up drawing canvas
      if (drawingCanvasRef.current) {
        drawingCanvasRef.current.width = state.selectedAspectRatio.width;
        drawingCanvasRef.current.height = state.selectedAspectRatio.height;
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to start recording. Please try again.',
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      stopMediaRecording(
        mediaRecorder,
        [displayStream.current, audioStream.current, webcamStream.current],
        animationFrame
      );

      // Clear stream references
      displayStream.current = null;
      audioStream.current = null;
      webcamStream.current = null;

      setState((prev) => ({ ...prev, isRecording: false }));

      // Handle the recorded chunks
      const blob = new Blob(chunks.current, { type: 'video/webm' });
      uploadToScreenApp(
        blob,
        'screen-recording.webm',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () =>
          setState((prev) => ({
            ...prev,
            uploadComplete: true,
          })),
        (error) => setState((prev) => ({ ...prev, error }))
      );
    }
  };

  const toggleWebcam = async (enabled: boolean) => {
    setState((prev) => ({ ...prev, showWebcam: enabled }));

    if (state.isRecording) {
      if (enabled) {
        try {
          webcamStream.current = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          if (webcamRef.current) {
            webcamRef.current.srcObject = webcamStream.current;
            webcamRef.current.play();
          }
        } catch (error) {
          setState((prev) => ({
            ...prev,
            showWebcam: false,
            error: 'Failed to enable webcam. Please try again.',
          }));
        }
      } else {
        if (webcamStream.current) {
          webcamStream.current.getTracks().forEach((track) => track.stop());
          webcamStream.current = null;
        }
        if (webcamRef.current) {
          webcamRef.current.srcObject = null;
        }
      }
    }
  };

  const toggleMicrophone = () => {
    if (audioStream.current) {
      const newMutedState = !state.isMuted;
      const tracks = audioStream.current.getAudioTracks();
      tracks.forEach((track) => {
        const newTrack = track.clone();
        newTrack.enabled = !newMutedState;
        audioStream.current?.addTrack(newTrack);
        track.stop();
      });
      setState((prev) => ({ ...prev, isMuted: newMutedState }));
    }
  };

  const togglePause = () => {
    if (mediaRecorder.current && state.isRecording) {
      if (state.isPaused) {
        mediaRecorder.current.resume();
      } else {
        mediaRecorder.current.pause();
      }
      setState((prev) => ({ ...prev, isPaused: !prev.isPaused }));
    }
  };

  const getScaledCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    const preview = previewRef.current;
    if (!canvas || !preview) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const videoAspect = preview.videoWidth / preview.videoHeight;
    const canvasAspect = canvas.width / canvas.height;

    // Calculate the actual dimensions and position of the video within the canvas
    let drawingWidth = canvas.width;
    let drawingHeight = canvas.height;
    let offsetX = 0;
    let offsetY = 0;

    if (videoAspect > canvasAspect) {
      // Video is wider than canvas
      drawingWidth = canvas.width;
      drawingHeight = canvas.width / videoAspect;
      offsetY = (canvas.height - drawingHeight) / 2;
    } else {
      // Video is taller than canvas
      drawingHeight = canvas.height;
      drawingWidth = canvas.height * videoAspect;
      offsetX = (canvas.width - drawingWidth) / 2;
    }

    // Calculate scale factors for the actual drawing area
    const scaleX = drawingWidth / rect.width;
    const scaleY = drawingHeight / rect.height;

    // Get mouse position relative to the canvas
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Scale the coordinates and adjust for offset
    return {
      x: mouseX * scaleX + offsetX,
      y: mouseY * scaleY + offsetY,
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!state.isDrawingMode) return;

    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const { x, y } = getScaledCoordinates(e);

    setDrawingState((prev) => ({
      ...prev,
      isDrawing: true,
      lastX: x,
      lastY: y,
      startX: x,
      startY: y,
    }));
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!state.isDrawingMode || !drawingState.isDrawing) return;

    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getScaledCoordinates(e);

    // Clear the canvas and redraw all previous paths
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawingState.paths.forEach((path) => {
      ctx.strokeStyle = drawingState.color;
      ctx.lineWidth = drawingState.lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke(path);
    });

    // Draw the current shape
    ctx.beginPath();
    ctx.strokeStyle = drawingState.color;
    ctx.lineWidth = drawingState.lineWidth;
    ctx.lineCap = 'round';

    if (drawingState.tool === 'pen') {
      ctx.moveTo(drawingState.lastX, drawingState.lastY);
      ctx.lineTo(x, y);
      ctx.stroke();

      const path = new Path2D();
      path.moveTo(drawingState.lastX, drawingState.lastY);
      path.lineTo(x, y);

      setDrawingState((prev) => ({
        ...prev,
        lastX: x,
        lastY: y,
        paths: [...prev.paths, path],
      }));
    } else if (drawingState.tool === 'rectangle') {
      const width = x - drawingState.startX;
      const height = y - drawingState.startY;
      ctx.strokeRect(drawingState.startX, drawingState.startY, width, height);
    } else if (drawingState.tool === 'circle') {
      const radius = Math.sqrt(
        (x - drawingState.startX) ** 2 + (y - drawingState.startY) ** 2
      );
      ctx.beginPath();
      ctx.arc(drawingState.startX, drawingState.startY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (!drawingState.isDrawing) return;

    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (drawingState.tool !== 'pen') {
      // For shapes, create a Path2D of the final shape
      const path = new Path2D();
      const { lastX, lastY } = drawingState;

      if (drawingState.tool === 'rectangle') {
        const width = lastX - drawingState.startX;
        const height = lastY - drawingState.startY;
        path.rect(drawingState.startX, drawingState.startY, width, height);
      } else if (drawingState.tool === 'circle') {
        const radius = Math.sqrt(
          (lastX - drawingState.startX) ** 2 +
            (lastY - drawingState.startY) ** 2
        );
        path.arc(
          drawingState.startX,
          drawingState.startY,
          radius,
          0,
          Math.PI * 2
        );
      }

      setDrawingState((prev) => ({
        ...prev,
        isDrawing: false,
        paths: [...prev.paths, path],
      }));
    } else {
      setDrawingState((prev) => ({
        ...prev,
        isDrawing: false,
      }));
    }
  };

  const clearDrawing = () => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawingState((prev) => ({ ...prev, paths: [] }));
  };

  const undoLastDraw = () => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const newPaths = [...drawingState.paths];
    newPaths.pop();

    // Redraw remaining paths
    newPaths.forEach((path) => {
      ctx.strokeStyle = drawingState.color;
      ctx.lineWidth = drawingState.lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke(path);
    });

    setDrawingState((prev) => ({
      ...prev,
      paths: newPaths,
    }));
  };

  const handleColorClick = (event: React.MouseEvent<HTMLElement>) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const handleReset = () => {
    // Reset all state to initial values
    setState({
      isRecording: false,
      showWebcam: false,
      isMuted: false,
      isPaused: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
      selectedAspectRatio: aspectRatioOptions[0],
      isDrawingMode: false,
    });

    // Reset drawing state
    setDrawingState({
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      color: '#F44336',
      lineWidth: 5,
      paths: [],
      tool: 'pen',
      startX: 0,
      startY: 0,
    });

    // Clear recorded blob
    recordedBlob.current = null;
    chunks.current = [];

    // Stop media recording and clear streams
    stopMediaRecording(mediaRecorder, [
      displayStream.current,
      audioStream.current,
      webcamStream.current,
    ]);
    displayStream.current = null;
    audioStream.current = null;
    webcamStream.current = null;

    // Clear canvas if it exists
    if (drawingCanvasRef.current) {
      const ctx = drawingCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          drawingCanvasRef.current.width,
          drawingCanvasRef.current.height
        );
      }
    }
  };

  const handleSaveAgain = () => {
    if (recordedBlob.current) {
      finishProcessing('https://screenapp.io/app/#/dashboard');
    }
  };

  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }

      // Stop all tracks
      const streams = [
        displayStream.current,
        audioStream.current,
        webcamStream.current,
      ];
      streams.forEach((stream) => {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      });
    };
  }, []);

  // Update the ToggleButton styles
  const toggleButtonStyles = {
    minWidth: 36,
    width: 36,
    height: 36,
    padding: 0,
    border: 'none',
    '&.MuiToggleButton-root': {
      border: 'none',
      borderRadius: 1.5,
    },
    '&.Mui-selected': {
      bgcolor: 'secondary.main',
      color: 'secondary.contrastText',
      '&:hover': {
        bgcolor: 'secondary.dark',
      },
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  };

  const iconButtonStyles = {
    minWidth: 36,
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 1.5,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  };

  const menuBarStyles = {
    bgcolor: 'background.paper',
    py: 1.5,
    px: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: 1,
    borderColor: 'divider',
    flexWrap: { xs: 'nowrap', sm: 'wrap' },
    gap: { xs: 2, sm: 2.5 },
    '& .MuiStack-root': {
      mx: 0,
    },
  };

  // Define menu items after all function declarations
  const menuItems: MenuItemBase[] = [
    // Recording controls group
    {
      id: 'record',
      icon: state.isRecording ? <Square /> : <Circle />,
      label: state.isRecording
        ? translate('screenRecorder.controls.stop')
        : translate('screenRecorder.controls.record'),
      tooltip: state.isRecording
        ? translate('screenRecorder.tooltips.stopRecording')
        : translate('screenRecorder.tooltips.startRecording'),
      onClick: state.isRecording ? stopRecording : startRecording,
      color: state.isRecording ? 'error' : 'error',
      show: () => true,
    },
    {
      id: 'pause',
      icon: state.isPaused ? <Play /> : <Pause />,
      label: state.isPaused
        ? translate('screenRecorder.controls.resume')
        : translate('screenRecorder.controls.pause'),
      tooltip: state.isPaused
        ? translate('screenRecorder.tooltips.resumeRecording')
        : translate('screenRecorder.tooltips.pauseRecording'),
      onClick: togglePause,
      color: state.isPaused ? 'primary' : 'inherit',
      show: (currentState) => currentState.isRecording,
    },
    {
      id: 'webcam',
      icon: state.showWebcam ? <Video /> : <VideoOff />,
      label: translate('screenRecorder.controls.webcam'),
      tooltip: state.showWebcam
        ? translate('screenRecorder.tooltips.disableWebcam')
        : translate('screenRecorder.tooltips.enableWebcam'),
      onClick: () => toggleWebcam(!state.showWebcam),
      color: state.showWebcam ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'toggle',
    },
    {
      id: 'microphone',
      icon: !state.isMuted ? <Mic /> : <MicOff />,
      label: translate('screenRecorder.controls.microphone'),
      tooltip: state.isMuted
        ? translate('screenRecorder.tooltips.unmuteMicrophone')
        : translate('screenRecorder.tooltips.muteMicrophone'),
      onClick: toggleMicrophone,
      color: !state.isMuted ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'toggle',
    },
    {
      id: 'aspectRatio',
      icon: <Maximize />,
      label: state.selectedAspectRatio.ratio,
      tooltip: translate('screenRecorder.tooltips.changeAspectRatio'),
      onClick: (e: React.MouseEvent<HTMLElement>) => handleAspectRatioClick(e),
      color: open ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'action',
    },
    {
      id: 'draw',
      icon: <Edit />,
      label: translate('screenRecorder.controls.draw'),
      tooltip: state.isDrawingMode
        ? translate('screenRecorder.tooltips.exitDrawingMode')
        : translate('screenRecorder.tooltips.enterDrawingMode'),
      onClick: () =>
        setState((prev) => ({ ...prev, isDrawingMode: !prev.isDrawingMode })),
      color: state.isDrawingMode ? 'secondary' : 'inherit',
      show: () => state.isRecording,
      buttonStyle: 'toggle',
    },
  ];

  // Drawing menu items in a separate array
  const drawingMenuItems: MenuItemBase[] = [
    {
      id: 'pen',
      icon: <Edit />,
      label: translate('screenRecorder.drawing.pen'),
      tooltip: translate('screenRecorder.drawing.pen'),
      onClick: () => setDrawingState((prev) => ({ ...prev, tool: 'pen' })),
      color: drawingState.tool === 'pen' ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'toggle',
    },
    {
      id: 'circle',
      icon: <CircleIcon />,
      label: translate('screenRecorder.drawing.circle'),
      tooltip: translate('screenRecorder.drawing.circle'),
      onClick: () => setDrawingState((prev) => ({ ...prev, tool: 'circle' })),
      color: drawingState.tool === 'circle' ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'toggle',
    },
    {
      id: 'rectangle',
      icon: <Rectangle />,
      label: translate('screenRecorder.drawing.rectangle'),
      tooltip: translate('screenRecorder.drawing.rectangle'),
      onClick: () =>
        setDrawingState((prev) => ({ ...prev, tool: 'rectangle' })),
      color: drawingState.tool === 'rectangle' ? 'secondary' : 'inherit',
      show: () => true,
      buttonStyle: 'toggle',
    },
    {
      id: 'color',
      icon: <Palette />,
      label: translate('screenRecorder.drawing.color'),
      tooltip: translate('screenRecorder.drawing.color'),
      onClick: (e: React.MouseEvent<HTMLElement>) => handleColorClick(e),
      show: () => true,
      buttonStyle: 'action',
    },
    {
      id: 'undo',
      icon: <Undo2 />,
      label: translate('screenRecorder.drawing.undo'),
      tooltip: translate('screenRecorder.drawing.undo'),
      onClick: undoLastDraw,
      show: () => true,
      buttonStyle: 'action',
      disabled: (_, currentDrawingState) =>
        currentDrawingState.paths.length === 0,
    },
    {
      id: 'clear',
      icon: <X />,
      label: translate('screenRecorder.drawing.clear'),
      tooltip: translate('screenRecorder.drawing.clear'),
      onClick: clearDrawing,
      show: () => true,
      buttonStyle: 'action',
      disabled: (_, currentDrawingState) =>
        currentDrawingState.paths.length === 0,
    },
  ];

  // Get visible menu items based on current state
  const visibleMenuItems = menuItems.filter((item) => item.show(state));

  // Update getVisibleAndOverflowItems to only return visible items
  const getVisibleItems = () => {
    if (isMobile) {
      // Show only record button in main menu on mobile
      return visibleMenuItems.slice(0, 1);
    }
    if (isTablet) {
      // Show essential controls on tablet
      return visibleMenuItems.slice(0, 4);
    }
    // Show all items on desktop
    return visibleMenuItems;
  };

  const visibleItems = getVisibleItems();

  // Update the drawing tools panel styles
  const drawingPanelStyles = {
    bgcolor: 'background.paper',
    p: 1.5,
    borderRadius: 2,
    border: 1,
    borderColor: 'divider',
  };

  return (
    <Box sx={{ p: 3, maxWidth: '60rem', mx: 'auto' }}>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={menuBarStyles}
        >
          {visibleItems.map((item) => (
            <React.Fragment key={item.id}>
              <Tooltip title={item.tooltip} placement="top">
                <Box>
                  <Stack
                    alignItems="center"
                    sx={{
                      minWidth: { xs: 'auto', sm: 48 },
                    }}
                  >
                    {item.buttonStyle === 'toggle' ? (
                      <ToggleButton
                        value={item.id}
                        selected={item.color === 'secondary'}
                        onChange={item.onClick}
                        size="small"
                        sx={toggleButtonStyles}
                      >
                        {item.icon}
                      </ToggleButton>
                    ) : (
                      <IconButton
                        color={
                          item.color as
                            | 'inherit'
                            | 'secondary'
                            | 'error'
                            | undefined
                        }
                        onClick={item.onClick}
                        size="small"
                        disabled={item.disabled?.(state, drawingState)}
                        sx={{
                          ...iconButtonStyles,
                          ...(item.color === 'error' && {
                            bgcolor: 'error.main',
                            '& .MuiSvgIcon-root': {
                              color: '#FFFFFF !important',
                            },
                            '&:hover': {
                              bgcolor: 'error.dark',
                              '& .MuiSvgIcon-root': {
                                color: '#FFFFFF !important',
                              },
                            },
                          }),
                          ...(item.color === 'secondary' && {
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            '&:hover': {
                              bgcolor: 'secondary.dark',
                            },
                          }),
                        }}
                      >
                        {item.icon}
                      </IconButton>
                    )}
                    <Typography
                      variant="caption"
                      color={
                        item.color === 'secondary'
                          ? 'secondary.contrastText'
                          : 'text.secondary'
                      }
                      sx={{
                        display: { xs: 'none', sm: 'block' },
                        fontSize: '0.675rem',
                        mt: 0.75,
                        lineHeight: 1,
                        opacity: 0.9,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                </Box>
              </Tooltip>
            </React.Fragment>
          ))}
        </Stack>

        {state.isRecording && state.isDrawingMode && (
          <Paper elevation={0} sx={drawingPanelStyles}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Edit />
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.secondary' }}
                >
                  Tools:
                </Typography>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  '& .MuiToggleButton-root': {
                    border: 'none',
                  },
                }}
              >
                {drawingMenuItems.map((item) => (
                  <Tooltip key={item.id} title={item.tooltip} placement="top">
                    {item.buttonStyle === 'toggle' ? (
                      <ToggleButton
                        value={item.id}
                        selected={item.color === 'secondary'}
                        onChange={item.onClick}
                        size="small"
                        sx={toggleButtonStyles}
                      >
                        {item.id === 'circle' ? <CircleIcon /> : item.icon}
                      </ToggleButton>
                    ) : (
                      <IconButton
                        color={
                          item.color as
                            | 'inherit'
                            | 'secondary'
                            | 'error'
                            | undefined
                        }
                        onClick={item.onClick}
                        size="small"
                        disabled={item.disabled?.(state, drawingState)}
                        sx={iconButtonStyles}
                      >
                        {item.icon}
                      </IconButton>
                    )}
                  </Tooltip>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>

      {state.error && (
        <Typography color="error" sx={{ mt: 2, mb: 2 }}>
          {state.error}
        </Typography>
      )}

      <Box
        sx={{
          position: 'relative',
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'hidden',
          aspectRatio:
            state.selectedAspectRatio.width / state.selectedAspectRatio.height,
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            position: 'relative',
          }}
        >
          {!state.isRecording && (
            <Typography>
              {translate('screenRecorder.messages.recordingPlaceholder')}
            </Typography>
          )}
          <video
            ref={previewRef}
            style={{
              width: '100%',
              height: '100%',
              display: state.isRecording ? 'block' : 'none',
              objectFit: 'contain',
            }}
            muted
          />
          <video
            ref={webcamRef}
            style={{
              position: 'absolute',
              bottom: 20,
              left: 20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              objectFit: 'cover',
              display: state.showWebcam && state.isRecording ? 'block' : 'none',
            }}
            muted
          />
          <canvas
            ref={drawingCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: state.isDrawingMode ? 'block' : 'none',
              cursor: drawingState.isDrawing ? 'none' : 'crosshair',
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onBlur={stopDrawing}
            tabIndex={0}
            aria-label="Drawing canvas for screen recording"
          />
        </Box>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleAspectRatioClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 250,
          },
        }}
      >
        {aspectRatioOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleAspectRatioSelect(option)}
            selected={state.selectedAspectRatio.id === option.id}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} secondary={option.ratio} />
          </MenuItem>
        ))}
      </Menu>

      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography gutterBottom>
            {translate('screenRecorder.drawing.lineColor')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              // Elegant primary colors
              '#2196F3', // Blue
              '#F44336', // Red
              '#4CAF50', // Green
              '#FFC107', // Amber

              // Muted tones
              '#607D8B', // Blue Grey
              '#795548', // Brown
              '#9C27B0', // Purple
              '#009688', // Teal

              // Pastels
              '#81D4FA', // Light Blue
              '#FFB74D', // Orange
              '#AED581', // Light Green
              '#F48FB1', // Pink

              // Essential
              '#000000', // Black
              '#FFFFFF', // White
              '#757575', // Grey
              '#FFEB3B', // Yellow
            ].map((color) => (
              <Tooltip key={color} title={color} arrow>
                <IconButton
                  onClick={() => {
                    setDrawingState((prev) => ({ ...prev, color }));
                    handleColorClose();
                  }}
                  sx={{
                    bgcolor: color,
                    width: 32,
                    height: 32,
                    border: (t) => `2px solid ${t.palette.divider}`,
                    '&:hover': {
                      bgcolor: color,
                      opacity: 0.9,
                    },
                    '&:focus': {
                      boxShadow: (t) =>
                        `0 0 0 2px ${t.palette.background.paper}, 0 0 0 4px ${color}`,
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <Typography gutterBottom>
            {translate('screenRecorder.drawing.lineWidth')}
          </Typography>
          <Slider
            value={drawingState.lineWidth}
            onChange={(_, value) =>
              setDrawingState((prev) => ({
                ...prev,
                lineWidth: value as number,
              }))
            }
            min={1}
            max={20}
            valueLabelDisplay="auto"
          />
        </Box>
      </Popover>

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={translate('screenRecorder.messages.processing')}
        completeMessage={translate('screenRecorder.messages.complete')}
        onReset={handleReset}
        onSaveAgain={handleSaveAgain}
      />
    </Box>
  );
};

export default ScreenRecorder;
